/**
 * VirtualTable Component
 * High-performance virtualized table using @tanstack/react-virtual
 *
 * Features:
 * - Virtual scrolling for 10,000+ rows at 60fps
 * - Sticky headers
 * - Row selection with checkbox support
 * - Sortable columns
 * - Custom cell rendering
 * - Performance monitoring
 * - Responsive design
 */

import React, { useRef, useState, useMemo, useCallback, useEffect, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronUp, ChevronDown, Search } from 'lucide-react';
import '../styles/virtual-scroll.css';

// Column definition interface
export interface VirtualTableColumn<T> {
  key: string;
  header: string;
  width?: number | string;
  minWidth?: number;
  render?: (item: T, index: number) => ReactNode;
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
}

// Sort state
export interface SortState {
  key: string;
  direction: 'asc' | 'desc';
}

// Props interface
export interface VirtualTableProps<T> {
  data: T[];
  columns: VirtualTableColumn<T>[];
  rowHeight?: number;
  height?: number | string;
  onRowClick?: (item: T, index: number) => void;
  getRowClassName?: (item: T, index: number) => string;
  keyExtractor: (item: T, index: number) => string | number;
  overscan?: number;
  selectable?: boolean;
  selectedRows?: Set<number>;
  onSelectionChange?: (selectedRows: Set<number>) => void;
  sortable?: boolean;
  sortState?: SortState;
  onSortChange?: (sort: SortState) => void;
  emptyMessage?: string;
  showPerformanceMetrics?: boolean;
  stickyHeader?: boolean;
  className?: string;
}

// Performance metrics interface
interface PerformanceMetrics {
  fps: number;
  renderTime: number;
  visibleRows: number;
  totalRows: number;
}

export function VirtualTable<T>({
  data,
  columns,
  rowHeight = 48,
  height = 600,
  onRowClick,
  getRowClassName,
  keyExtractor,
  overscan = 15,
  selectable = false,
  selectedRows = new Set(),
  onSelectionChange,
  sortable = false,
  sortState,
  onSortChange,
  emptyMessage = 'No data available',
  showPerformanceMetrics = false,
  stickyHeader = true,
  className = '',
}: VirtualTableProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    renderTime: 0,
    visibleRows: 0,
    totalRows: data.length,
  });
  const frameCount = useRef(0);
  const lastTime = useRef(performance.now());

  // Calculate column widths
  const columnWidths = useMemo(() => {
    const totalSpecified = columns.reduce((acc, col) => {
      if (typeof col.width === 'number') return acc + col.width;
      return acc;
    }, 0);
    const checkboxWidth = selectable ? 40 : 0;
    const unspecifiedCount = columns.filter(col => !col.width).length;
    const remainingWidth = unspecifiedCount > 0 ? `calc((100% - ${totalSpecified + checkboxWidth}px) / ${unspecifiedCount})` : 'auto';

    return columns.map(col => {
      if (typeof col.width === 'number') return `${col.width}px`;
      if (typeof col.width === 'string') return col.width;
      return remainingWidth;
    });
  }, [columns, selectable]);

  // Virtual row configuration
  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan,
    measureElement: undefined, // Use fixed height for performance
  });

  // Performance monitoring
  useEffect(() => {
    if (!showPerformanceMetrics) return undefined;

    let animationFrameId: number;
    const measureFPS = (): void => {
      frameCount.current++;
      const currentTime = performance.now();
      const elapsed = currentTime - lastTime.current;

      if (elapsed >= 1000) {
        setMetrics(prev => ({
          ...prev,
          fps: Math.round((frameCount.current * 1000) / elapsed),
          visibleRows: rowVirtualizer.getVirtualItems().length,
          totalRows: data.length,
        }));
        frameCount.current = 0;
        lastTime.current = currentTime;
      }

      animationFrameId = requestAnimationFrame(measureFPS);
    };

    animationFrameId = requestAnimationFrame(measureFPS);
    return () => cancelAnimationFrame(animationFrameId);
  }, [showPerformanceMetrics, data.length, rowVirtualizer]);

  // Handle sort
  const handleSort = useCallback((columnKey: string) => {
    if (!sortable || !onSortChange) return;

    const column = columns.find(c => c.key === columnKey);
    if (!column?.sortable) return;

    const newDirection = sortState?.key === columnKey && sortState.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key: columnKey, direction: newDirection });
  }, [sortable, sortState, onSortChange, columns]);

  // Handle row selection
  const handleRowSelect = useCallback((index: number, event: React.MouseEvent | React.ChangeEvent) => {
    event.stopPropagation();
    if (!onSelectionChange) return;

    const newSelection = new Set(selectedRows);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    onSelectionChange(newSelection);
  }, [selectedRows, onSelectionChange]);

  // Handle select all
  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;

    if (selectedRows.size === data.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(data.map((_, i) => i)));
    }
  }, [selectedRows, data, onSelectionChange]);

  // Empty state
  if (data.length === 0) {
    return (
      <div className={`virtual-table-container ${className}`}>
        <div className="virtual-scroll-empty">
          <Search className="virtual-scroll-empty-icon" />
          <div className="virtual-scroll-empty-text">{emptyMessage}</div>
        </div>
      </div>
    );
  }

  const virtualItems = rowVirtualizer.getVirtualItems();

  return (
    <div className={`virtual-table-container ${className}`} style={{ position: 'relative' }}>
      {/* Sticky Header */}
      {stickyHeader && (
        <div className="virtual-table-header">
          {selectable && (
            <div className="virtual-cell-checkbox" style={{ width: 40, flexShrink: 0 }}>
              <input
                type="checkbox"
                checked={selectedRows.size === data.length && data.length > 0}
                onChange={handleSelectAll}
                title="Select all"
              />
            </div>
          )}
          {columns.map((column, colIndex) => (
            <div
              key={column.key}
              className={`virtual-table-header-cell ${column.sortable && sortable ? 'sortable' : ''} ${
                sortState?.key === column.key ? `sorted ${sortState.direction}` : ''
              } ${column.headerClassName || ''}`}
              style={{ width: columnWidths[colIndex], minWidth: column.minWidth }}
              onClick={() => column.sortable && handleSort(column.key)}
            >
              {column.header}
              {sortState?.key === column.key && (
                sortState.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Virtual Scroll Container */}
      <div
        ref={parentRef}
        className="virtual-scroll-container"
        style={{ height: typeof height === 'number' ? `${height}px` : height }}
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualItems.map((virtualRow) => {
            const item = data[virtualRow.index];
            const rowClass = getRowClassName ? getRowClassName(item, virtualRow.index) : '';
            const isSelected = selectedRows.has(virtualRow.index);

            return (
              <div
                key={keyExtractor(item, virtualRow.index)}
                className={`virtual-row ${rowClass} ${isSelected ? 'selected' : ''}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                onClick={() => onRowClick?.(item, virtualRow.index)}
              >
                {selectable && (
                  <div className="virtual-cell-checkbox" style={{ width: 40, flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleRowSelect(virtualRow.index, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
                {columns.map((column, colIndex) => (
                  <div
                    key={column.key}
                    className={`virtual-cell ${column.className || ''}`}
                    style={{ width: columnWidths[colIndex], minWidth: column.minWidth }}
                  >
                    {column.render
                      ? column.render(item, virtualRow.index)
                      : String((item as Record<string, unknown>)[column.key] ?? '')}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selection Bar */}
      {selectable && selectedRows.size > 0 && (
        <div className="virtual-scroll-selection-bar">
          <span>{selectedRows.size} row{selectedRows.size !== 1 ? 's' : ''} selected</span>
          <button onClick={() => onSelectionChange?.(new Set())}>
            Clear Selection
          </button>
        </div>
      )}

      {/* Performance Metrics */}
      {showPerformanceMetrics && (
        <div className={`virtual-scroll-perf visible ${metrics.fps < 30 ? 'danger' : metrics.fps < 50 ? 'warning' : ''}`}>
          {metrics.fps} FPS | {metrics.visibleRows}/{metrics.totalRows} rows
        </div>
      )}
    </div>
  );
}

export default VirtualTable;
